import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Menu as MenuIcon, ChevronRight } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const iconOptions = [
  'Home','Database','Package','FileText','Settings',
  'Users','User','Globe','MapPin','Building',
  'DollarSign','Truck','Plane','Ship','Send',
  'BarChart','PieChart','List','Grid','Layout',
  'Folder','File','Calendar','Clock','Bell',
  'Mail','Phone','Camera','Image','Video',
  'Music','Mic','Speaker','Monitor','Smartphone',
  'Tablet','Laptop','Printer','Save','Download',
  'Upload','Cloud','Lock','Unlock','Key',
  'Shield','AlertCircle','Info','CheckCircle','XCircle',
];

const MenuItems = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const [menuItems,         setMenuItems]         = useState([]);
  const [allMenus,          setAllMenus]          = useState([]);
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [searchTerm,        setSearchTerm]        = useState('');
  const [showModal,         setShowModal]         = useState(false);
  const [editMode,          setEditMode]          = useState(false);
  const [submitLoading,     setSubmitLoading]     = useState(false);
  const [errors,            setErrors]            = useState({});

  const [formData, setFormData] = useState({
    menuid: null, menucode: '', menuname: '', parentmenuid: '',
    iconname: '', menuorder: 0, isactive: true,
  });

  useEffect(() => { fetchMenuItems(); }, []);
  useEffect(() => { filterMenuItems(); }, [searchTerm, menuItems]);

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/MenuItems.php', { params: { active: true } });
      if (res.data.success) { const m = res.data.data || []; setMenuItems(m); setAllMenus(m); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
    } finally { setLoading(false); }
  };

  const filterMenuItems = () => {
    if (!searchTerm.trim()) { setFilteredMenuItems(menuItems); return; }
    const q = searchTerm.toLowerCase();
    setFilteredMenuItems(menuItems.filter(m =>
      m.menuname.toLowerCase().includes(q) ||
      m.menucode.toLowerCase().includes(q) ||
      m.parentmenuname?.toLowerCase().includes(q)
    ));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.menucode.trim()) errs.menucode = 'Menu code is required';
    else if (formData.menucode.length > 50) errs.menucode = 'Menu code must be 50 characters or less';
    if (!formData.menuname.trim()) errs.menuname = 'Menu name is required';
    else if (formData.menuname.length > 100) errs.menuname = 'Menu name must be 100 characters or less';
    if (formData.iconname && formData.iconname.length > 50) errs.iconname = 'Icon name must be 50 characters or less';
    if (editMode && formData.parentmenuid && formData.parentmenuid === formData.menuid)
      errs.parentmenuid = 'A menu cannot be its own parent';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const res = await apiClient[method]('/MenuItems.php', {
        ...formData,
        parentmenuid: formData.parentmenuid !== '' ? parseInt(formData.parentmenuid) : null,
        menuorder: parseInt(formData.menuorder) || 0,
        isactive: formData.isactive ? 1 : 0,
      });
      if (res.data.success) {
        alert(editMode ? 'Menu item updated successfully!' : 'Menu item created successfully!');
        closeModal(); fetchMenuItems();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else if (err.response?.status === 409) alert('Menu code already exists. Please use a different code.');
      else alert('Error saving menu item. Please try again.');
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (item) => {
    setFormData({ menuid: item.menuid, menucode: item.menucode, menuname: item.menuname, parentmenuid: item.parentmenuid || '', iconname: item.iconname || '', menuorder: item.menuorder, isactive: item.isactive === 1 });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id, hasKids) => {
    if (hasKids) { alert('Cannot delete menu with submenus. Please delete or reassign submenus first.'); return; }
    if (!window.confirm('Are you sure you want to delete this menu item?')) return;
    try {
      const res = await apiClient.delete('/MenuItems.php', { data: { menuid: id } });
      if (res.data.success) { alert('Menu item deleted successfully!'); fetchMenuItems(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else if (err.response?.status === 409) alert('Cannot delete menu with submenus.');
      else alert('Error deleting menu item. Please try again.');
    }
  };

  const resetForm    = () => { setFormData({ menuid: null, menucode: '', menuname: '', parentmenuid: '', iconname: '', menuorder: 0, isactive: true }); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };
  const hasChildren  = (id) => menuItems.some(m => m.parentmenuid === id);

  const getAvailableParentMenus = () => {
    if (!editMode) return allMenus;
    const getDescendantIds = (id) => {
      const kids = allMenus.filter(m => m.parentmenuid === id);
      return kids.reduce((acc, c) => [...acc, c.menuid, ...getDescendantIds(c.menuid)], []);
    };
    const excluded = [formData.menuid, ...getDescendantIds(formData.menuid)];
    return allMenus.filter(m => !excluded.includes(m.menuid));
  };

  const groupMenusByParent = (menus) => {
    const parents  = menus.filter(m => !m.parentmenuid);
    const children = menus.filter(m => m.parentmenuid);
    return parents.map(p => ({ ...p, children: children.filter(c => c.parentmenuid === p.menuid) }));
  };

  const availableParentMenus = getAvailableParentMenus();
  const groupedMenus = groupMenusByParent(availableParentMenus);

  const inputCls  = (err) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const selectCls = (err) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const labelCls  = 'block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide';
  const errMsg    = (msg) => msg ? <p className="text-xs text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <MenuIcon size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Menu Items Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage navigation menu structure and hierarchy</p>
          </div>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Menu Item
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by menu name, code, or parent menu..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredMenuItems.length} {filteredMenuItems.length === 1 ? 'menu' : 'menus'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading menu items...</div>
        ) : filteredMenuItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <MenuIcon size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No menu items found</p>
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear search</button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse" style={{ minWidth: 900 }}>
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Menu Code', 'Menu Name', 'Parent Menu', 'Icon', 'Order', 'Status', 'Created At', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredMenuItems.map(item => (
                  <tr key={item.menuid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
                        {item.menucode}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-gray-800">
                      {item.parentmenuid && (
                        <ChevronRight size={14} className="inline mr-1 text-gray-400" />
                      )}
                      {item.menuname}
                    </td>
                    <td className="px-4 py-3.5">
                      {item.parentmenuname ? (
                        <span className="inline-block px-2.5 py-1 bg-blue-50 text-blue-700 border-2 border-blue-200 rounded-lg text-[12px] font-bold">
                          {item.parentmenucode} — {item.parentmenuname}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic text-sm">Main Menu</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 font-mono text-sm text-gray-600">{item.iconname || '-'}</td>
                    <td className="px-4 py-3.5 text-center text-gray-600 font-semibold">{item.menuorder}</td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        item.isactive ? 'bg-emerald-50 text-emerald-700 border-emerald-400' : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {item.isactive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">
                      {new Date(item.createdat).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3.5 sticky right-0 bg-white shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.08)]">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(item)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(item.menuid, hasChildren(item.menuid))} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[600px] shadow-2xl border border-gray-200 overflow-hidden max-h-[90vh] flex flex-col"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <MenuIcon size={18} /> {editMode ? 'Edit Menu Item' : 'Add New Menu Item'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-7">

              {/* Code + Name */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Menu Code <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.menucode}
                    onChange={e => setFormData({ ...formData, menucode: e.target.value.toUpperCase() })}
                    maxLength="50" placeholder="e.g., CUSTOMERS, REPORTS"
                    className={inputCls(errors.menucode)} />
                  {errMsg(errors.menucode)}
                  <p className="text-[11px] text-gray-400 mt-1">Enter unique code (e.g., VEHICLES, USER_MANAGEMENT)</p>
                </div>
                <div>
                  <label className={labelCls}>Menu Name <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.menuname}
                    onChange={e => setFormData({ ...formData, menuname: e.target.value })}
                    maxLength="100" placeholder="e.g., Dashboard, Reports"
                    className={inputCls(errors.menuname)} />
                  {errMsg(errors.menuname)}
                </div>
              </div>

              {/* Parent + Icon */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>
                    Parent Menu
                    {editMode && formData.menuid && (
                      <span className="text-gray-400 text-[10px] font-normal ml-1 normal-case">(Circular refs prevented)</span>
                    )}
                  </label>
                  <select value={formData.parentmenuid}
                    onChange={e => setFormData({ ...formData, parentmenuid: e.target.value })}
                    className={selectCls(errors.parentmenuid)}>
                    <option value="">-- None (Main Menu) --</option>
                    {groupedMenus.map(parent => (
                      <optgroup key={parent.menuid} label={`${parent.menucode} - ${parent.menuname}`}>
                        <option value={parent.menuid}>{parent.menucode} - {parent.menuname}</option>
                        {parent.children.map(child => (
                          <option key={child.menuid} value={child.menuid}>
                            &nbsp;&nbsp;↳ {child.menucode} - {child.menuname}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {errMsg(errors.parentmenuid)}
                  <p className="text-[11px] text-gray-400 mt-1">You can nest menus multiple levels deep</p>
                </div>
                <div>
                  <label className={labelCls}>Icon Name (lucide-react)</label>
                  <select value={formData.iconname}
                    onChange={e => setFormData({ ...formData, iconname: e.target.value })}
                    className={selectCls(errors.iconname)}>
                    <option value="">-- No Icon --</option>
                    {iconOptions.map(icon => <option key={icon} value={icon}>{icon}</option>)}
                  </select>
                  {errMsg(errors.iconname)}
                  <p className="text-[11px] text-gray-400 mt-1">Icons from lucide-react library</p>
                </div>
              </div>

              {/* Order + Active */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className={labelCls}>Display Order</label>
                  <input type="number" value={formData.menuorder} min="0" placeholder="0"
                    onChange={e => setFormData({ ...formData, menuorder: parseInt(e.target.value) || 0 })}
                    className={inputCls(false) + ' text-right'} />
                  <p className="text-[11px] text-gray-400 mt-1">Lower numbers appear first</p>
                </div>
                <div className="flex items-end pb-0.5">
                  <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg w-full hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                    <input type="checkbox" checked={formData.isactive}
                      onChange={e => setFormData({ ...formData, isactive: e.target.checked })}
                      className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                    <span className="text-sm font-semibold text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default MenuItems;